import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formMappings } from "../../../../config/formMappings";
import { FormEntry } from "../../../../types/types";

interface AverageTablesPerRegistrationProps {
  v2Entries: FormEntry[];
}

export default function PercentageTableRegistrations({
  v2Entries,
}: AverageTablesPerRegistrationProps) {
  const entries = useSelector((state: RootState) => state.formEntries.entries);
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const bazaarTableMapping =
    formMapping && "bazaarTable" in formMapping
      ? formMapping.bazaarTable?.fieldMappings
      : undefined;

  // Calculate the percentage of paid registrations that need a table
  let totalPaidRegistrations: number;
  let registrationsNeedingTable: number;

  if (isVersion1) {
    // For v1, use Redux entries for both total and table count
    const paidEntries = entries.filter((entry) => {
      const paidStatus =
        entry.meta["Paid?"] ||
        entry.meta[formMapping?.fieldMappings?.paid || ""] ||
        "No";
      return paidStatus === "Yes";
    });

    totalPaidRegistrations = paidEntries.length;
    registrationsNeedingTable = paidEntries.filter(
      (entry) => entry.meta["Will you be needing a table for bazaar?"] === "Yes"
    ).length;
  } else {
    // For v2, get total paid registrations from Redux state
    const paidEntriesFromRedux = entries.filter((entry) => {
      const paidStatus =
        entry.meta[formMapping?.fieldMappings?.paid || ""] ||
        entry.meta["Paid?"] ||
        "No";
      return paidStatus === "Yes";
    });
    totalPaidRegistrations = paidEntriesFromRedux.length;

    // Get registrations needing table from bazaar table API entries (only paid ones)
    registrationsNeedingTable = v2Entries.filter((entry) => {
      if (!bazaarTableMapping) return false;

      // Check if registered for conference
      const registeredField =
        entry.meta[bazaarTableMapping.registeredForConference] ||
        entry.meta["Will you be needing a table for bazaar?"];
      if (registeredField !== "Yes") return false;

      // Check if paid
      const paidStatusRaw =
        entry.meta[bazaarTableMapping.paid] || entry.meta["Paid?"] || "No";
      const paidStatus = Array.isArray(paidStatusRaw)
        ? paidStatusRaw[0]
        : paidStatusRaw;
      return paidStatus === "Yes";
    }).length;
  }
  const percentage =
    totalPaidRegistrations > 0
      ? (registrationsNeedingTable / totalPaidRegistrations) * 100
      : 0;

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
      <dt className="truncate text-sm font-medium text-gray-500">
        Average Registrants Needing Table
      </dt>
      <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
        {percentage.toFixed(2)}%
      </dd>
    </div>
  );
}
